import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  CheckCircle,
  XCircle,
  ImageSquare,
  Tag,
  Globe,
  Sparkle,
  Palette,
  Ruler,
  FileImage,
  TrendUp,
  Warning,
} from "@phosphor-icons/react";

const money = (value, currency) =>
  value === null || value === undefined || value === ""
    ? "—"
    : `${currency === "USD" ? "$" : ""}${value}${currency && currency !== "USD" ? " " + currency : ""}`;

const fileSize = (bytes) => {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

const placementLabel = (type) =>
  String(type || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Slide-over panel with everything Printful knows about one store product:
// the blank it prints on, every synced variant with retail/wholesale/margin,
// colour swatches, stock by region, and the print files per placement.
const ProductDetail = ({ data, loading, error, onClose, onRetry }) => {
  const [tab, setTab] = useState("variants");
  const [activeVariantId, setActiveVariantId] = useState(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);

  const product = data?.product;
  const catalog = data?.catalog;
  const summary = data?.summary;
  const variants = useMemo(() => data?.variants || [], [data]);

  useEffect(() => {
    setActiveVariantId(variants[0]?.id ?? null);
    setTab("variants");
  }, [variants]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const activeVariant =
    variants.find((v) => v.id === activeVariantId) || variants[0] || null;
  const currency = summary?.currency || "USD";

  const styles = `
    .pfd {
      --pf-ink: #1C1917; --pf-gold: #CA8A04; --pf-bg: #FAFAF9;
      --pf-text: #0C0A09; --pf-muted: #57534E; --pf-line: #E7E5E4;
      font-family: 'Nunito Sans', system-ui, sans-serif; color: var(--pf-text);
    }
    .pfd h1,.pfd h2,.pfd h3,.pfd h4,.pfd h5 { font-family: 'Rubik', system-ui, sans-serif; letter-spacing: -.01em; }
    .pfd-backdrop {
      position: fixed; inset: 0; background: rgba(12,10,9,.55); z-index: 1080;
      animation: pfd-fade .18s ease;
    }
    .pfd-panel {
      position: fixed; top: 0; right: 0; bottom: 0; width: min(1020px, 100%);
      background: var(--pf-bg); z-index: 1085; display: flex; flex-direction: column;
      box-shadow: -20px 0 56px rgba(12,10,9,.26);
      animation: pfd-slide .26s cubic-bezier(.22,.8,.3,1);
    }
    @keyframes pfd-fade { from { opacity: 0 } to { opacity: 1 } }
    @keyframes pfd-slide { from { transform: translateX(30px); opacity: .55 } to { transform: none; opacity: 1 } }

    .pfd-head {
      background:
        radial-gradient(700px 240px at 92% -60%, rgba(202,138,4,.30), transparent 60%),
        linear-gradient(135deg,#1C1917,#292524);
      color: #fff;
    }
    .pfd-kpi { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.15); border-radius: .85rem; min-width: 96px; }
    .pfd-kpi-v { font-family: 'Rubik', sans-serif; font-size: 1.02rem; line-height: 1.2; }
    .pfd-kpi-l { font-size: .68rem; letter-spacing: .04em; text-transform: uppercase; opacity: .72; }

    .pfd-tabs { background: #fff; border-bottom: 1px solid var(--pf-line); }
    .pfd-tab {
      border: none; background: none; padding: .85rem .15rem; margin-right: 1.5rem;
      font-size: .88rem; font-weight: 600; color: var(--pf-muted);
      border-bottom: 2px solid transparent; min-height: 44px;
    }
    .pfd-tab:hover { color: var(--pf-text); }
    .pfd-tab.active { color: var(--pf-text); border-bottom-color: var(--pf-gold); }
    .pfd :focus-visible { outline: 2px solid var(--pf-gold); outline-offset: 2px; }

    .pfd-body { overflow-y: auto; }
    .pfd-card { background: #fff; border: 1px solid var(--pf-line); border-radius: 1rem; }
    .pfd-stage {
      background: #fff; border: 1px solid var(--pf-line); border-radius: 1rem;
      aspect-ratio: 1/1; display: flex; align-items: center; justify-content: center; overflow: hidden;
    }
    .pfd-stage img { width: 100%; height: 100%; object-fit: contain; }

    .pfd-swatch { width: 15px; height: 15px; border-radius: 50%; border: 1px solid rgba(12,10,9,.18); display: inline-block; flex-shrink: 0; }
    .pfd-vcard {
      display: flex; align-items: center; gap: .85rem; width: 100%; text-align: left;
      border: 1px solid var(--pf-line); border-radius: .9rem; background: #fff; padding: .6rem; cursor: pointer;
      transition: border-color .2s ease, box-shadow .2s ease;
    }
    .pfd-vcard:hover { border-color: rgba(202,138,4,.55); }
    .pfd-vcard.active { border-color: var(--pf-ink); box-shadow: 0 0 0 2px rgba(28,25,23,.09); }
    .pfd-vthumb { width: 68px; height: 68px; border-radius: .6rem; background: #F5F5F4; object-fit: contain; flex-shrink: 0; }

    .pfd-file { border: 1px solid var(--pf-line); border-radius: .9rem; background: #fff; overflow: hidden; height: 100%; }
    .pfd-file img { width: 100%; height: 132px; object-fit: contain; background: #F5F5F4; }

    .pfd-pill { font-size: .72rem; font-weight: 600; padding: .18rem .5rem; border-radius: 999px; }
    .pfd-ok { background: #ECFDF5; color: #047857; }
    .pfd-no { background: #FEF2F2; color: #B91C1C; }
    .pfd-mute { background: #F5F5F4; color: var(--pf-muted); }
    .pfd-gold { background: #FEF7E6; color: #A16207; }

    @media (prefers-reduced-motion: reduce) {
      .pfd-panel, .pfd-backdrop { animation: none; }
      .pfd * { transition-duration: .001ms !important; }
    }
  `;

  const Kpi = ({ label, value }) => (
    <div className="pfd-kpi px-3 py-2">
      <div className="pfd-kpi-v fw-bold">{value}</div>
      <div className="pfd-kpi-l">{label}</div>
    </div>
  );

  const Meta = ({ icon, label, value }) => (
    <div className="d-flex align-items-start gap-2 py-2">
      <span style={{ color: "#78716C", marginTop: 2 }} aria-hidden="true">{icon}</span>
      <span className="min-width-0">
        <span className="d-block" style={{ fontSize: ".72rem", color: "#78716C" }}>{label}</span>
        <span className="d-block fw-semibold" style={{ fontSize: ".86rem" }}>{value || "—"}</span>
      </span>
    </div>
  );

  const priceRange = (min, max) =>
    min === max ? money(min, currency) : `${money(min, currency)} – ${max}`;

  return (
    <div className="pfd">
      <style>{styles}</style>
      <div className="pfd-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="pfd-panel"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={product?.name || "Product details"}
      >
        {/* Header */}
        <div className="pfd-head p-3 p-lg-4">
          <div className="d-flex align-items-start gap-3">
            <div
              className="rounded-3 overflow-hidden flex-shrink-0 d-flex align-items-center justify-content-center"
              style={{ width: 64, height: 64, background: "rgba(255,255,255,.1)" }}
            >
              {product?.thumbnail_url ? (
                <img
                  src={product.thumbnail_url}
                  alt={product.name}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <ImageSquare size={26} weight="thin" aria-hidden="true" />
              )}
            </div>

            <div className="flex-grow-1 min-width-0">
              <h5 className="fw-bold mb-1 text-truncate" title={product?.name}>
                {loading ? "Loading product…" : product?.name || "Product"}
              </h5>
              <div style={{ fontSize: ".84rem", opacity: 0.8 }}>
                {catalog?.type_name || catalog?.title || ""}
                {catalog?.brand ? ` · ${catalog.brand}` : ""}
              </div>
            </div>

            <button
              ref={closeRef}
              type="button"
              className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 38, height: 38 }}
              onClick={onClose}
              aria-label="Close product details"
            >
              <X size={16} weight="bold" aria-hidden="true" />
            </button>
          </div>

          {summary && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              <Kpi label="Variants" value={summary.variant_count} />
              <Kpi label="Retail" value={priceRange(summary.retail_min, summary.retail_max)} />
              <Kpi label="Your cost" value={priceRange(summary.cost_min, summary.cost_max)} />
              <Kpi
                label="Avg margin"
                value={summary.avg_margin_percent !== null ? `${summary.avg_margin_percent}%` : "—"}
              />
              <Kpi label="In stock" value={`${summary.in_stock}/${summary.variant_count}`} />
            </div>
          )}
        </div>

        {/* Tabs */}
        {!loading && !error && data && (
          <div className="pfd-tabs px-3 px-lg-4 d-flex" role="tablist">
            {[
              { key: "variants", label: `Variants (${variants.length})` },
              { key: "files", label: `Print files (${activeVariant?.files?.length || 0})` },
              { key: "about", label: "Product info" },
            ].map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                className={`pfd-tab ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="pfd-body flex-grow-1 p-3 p-lg-4">
          {loading && (
            <div className="text-center py-5" style={{ color: "#57534E" }} aria-live="polite">
              <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Loading full product details…
            </div>
          )}

          {!loading && error && (
            <div className="pfd-card text-center py-5 px-3">
              <Warning size={36} weight="fill" className="mb-2" style={{ color: "#B91C1C" }} aria-hidden="true" />
              <div className="fw-semibold mb-1">Couldn't load this product</div>
              <div className="mb-3" style={{ fontSize: ".86rem", color: "#57534E" }}>{error}</div>
              <button className="btn btn-sm btn-dark rounded-3" onClick={onRetry}>Try again</button>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Variants */}
              {tab === "variants" && (
                <div className="row g-3 g-lg-4">
                  <div className="col-lg-5">
                    <div className="pfd-stage mb-3">
                      {activeVariant?.image ? (
                        <img src={activeVariant.image} alt={activeVariant.name} />
                      ) : (
                        <ImageSquare size={40} weight="thin" style={{ color: "#A8A29E" }} aria-hidden="true" />
                      )}
                    </div>

                    {activeVariant && (
                      <div className="pfd-card p-3">
                        <div className="fw-bold mb-2" style={{ fontSize: ".95rem" }}>
                          {activeVariant.size}
                          {activeVariant.color ? ` · ${activeVariant.color}` : ""}
                        </div>

                        <div className="d-flex justify-content-between py-1" style={{ fontSize: ".85rem" }}>
                          <span style={{ color: "#57534E" }}>Retail</span>
                          <span className="fw-bold">{money(activeVariant.retail_price, activeVariant.currency)}</span>
                        </div>
                        <div className="d-flex justify-content-between py-1" style={{ fontSize: ".85rem" }}>
                          <span style={{ color: "#57534E" }}>Your cost</span>
                          <span>{money(activeVariant.cost, currency)}</span>
                        </div>
                        <div
                          className="d-flex justify-content-between align-items-center py-2 px-2 mt-2 rounded-3"
                          style={{ background: "#FEF7E6", fontSize: ".85rem" }}
                        >
                          <span className="fw-semibold" style={{ color: "#A16207" }}>Profit</span>
                          <span className="fw-bold d-inline-flex align-items-center gap-1" style={{ color: "#A16207" }}>
                            <TrendUp size={13} weight="bold" aria-hidden="true" />
                            {money(activeVariant.margin, currency)}
                            {activeVariant.margin_percent !== null && (
                              <span className="fw-normal">({activeVariant.margin_percent}%)</span>
                            )}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between py-1 mt-2" style={{ fontSize: ".82rem" }}>
                          <span style={{ color: "#57534E" }}>SKU</span>
                          <span className="text-truncate ms-2" title={activeVariant.sku}>{activeVariant.sku || "—"}</span>
                        </div>
                        <div className="d-flex justify-content-between py-1" style={{ fontSize: ".82rem" }}>
                          <span style={{ color: "#57534E" }}>Ships from</span>
                          <span>{Object.values(activeVariant.availability_regions || {}).join(", ") || "—"}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-lg-7">
                    <div className="d-flex flex-column gap-2">
                      {variants.map((v) => (
                        <button
                          type="button"
                          key={v.id}
                          className={`pfd-vcard ${v.id === activeVariantId ? "active" : ""}`}
                          onClick={() => setActiveVariantId(v.id)}
                          aria-pressed={v.id === activeVariantId}
                        >
                          {v.image ? (
                            <img src={v.image} alt={v.name} className="pfd-vthumb" loading="lazy" decoding="async" />
                          ) : (
                            <span className="pfd-vthumb d-flex align-items-center justify-content-center" style={{ color: "#A8A29E" }}>
                              <ImageSquare size={22} weight="thin" aria-hidden="true" />
                            </span>
                          )}

                          <span className="flex-grow-1 min-width-0">
                            <span className="d-flex align-items-center gap-2 mb-1">
                              <span className="fw-bold" style={{ fontSize: ".9rem" }}>{v.size || "One size"}</span>
                              {v.color && (
                                <span className="d-inline-flex align-items-center gap-1" style={{ fontSize: ".78rem", color: "#57534E" }}>
                                  <span className="pfd-swatch" style={{ background: v.color_code || "#E7E5E4" }} />
                                  {v.color}
                                </span>
                              )}
                            </span>
                            <span className="d-block text-truncate" style={{ fontSize: ".74rem", color: "#78716C" }}>
                              {v.sku}
                            </span>
                          </span>

                          <span className="text-end flex-shrink-0">
                            <span className="d-block fw-bold" style={{ fontSize: ".9rem" }}>
                              {money(v.retail_price, v.currency)}
                            </span>
                            <span className="d-block" style={{ fontSize: ".72rem", color: "#78716C" }}>
                              cost {money(v.cost, currency)}
                            </span>
                          </span>

                          <span className="flex-shrink-0 d-flex flex-column align-items-end gap-1">
                            {v.margin_percent !== null && (
                              <span className="pfd-pill pfd-gold">+{v.margin_percent}%</span>
                            )}
                            {v.in_stock === true && (
                              <span className="pfd-pill pfd-ok d-inline-flex align-items-center gap-1">
                                <CheckCircle size={11} weight="fill" aria-hidden="true" /> In stock
                              </span>
                            )}
                            {v.in_stock === false && (
                              <span className="pfd-pill pfd-no d-inline-flex align-items-center gap-1">
                                <XCircle size={11} weight="fill" aria-hidden="true" /> Out
                              </span>
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Print files */}
              {tab === "files" && (
                <div className="row g-3">
                  {(activeVariant?.files || []).length === 0 && (
                    <div style={{ fontSize: ".88rem", color: "#57534E" }}>No print files on this variant.</div>
                  )}
                  {(activeVariant?.files || []).map((f) => (
                    <div className="col-6 col-md-4 col-xl-3" key={f.id}>
                      <div className="pfd-file">
                        {f.thumbnail_url ? (
                          <img src={f.thumbnail_url} alt={`${placementLabel(f.type)} print file`} loading="lazy" decoding="async" />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center" style={{ height: 132, background: "#F5F5F4", color: "#A8A29E" }}>
                            <FileImage size={26} weight="thin" aria-hidden="true" />
                          </div>
                        )}
                        <div className="p-2 border-top" style={{ borderColor: "#E7E5E4" }}>
                          <div className="fw-bold mb-1" style={{ fontSize: ".82rem" }}>{placementLabel(f.type)}</div>
                          <div style={{ fontSize: ".72rem", color: "#78716C" }}>
                            {f.width}×{f.height}px · {f.dpi} DPI
                          </div>
                          <div style={{ fontSize: ".72rem", color: "#78716C" }}>
                            {fileSize(f.size)} · {(f.mime_type || "").replace("image/", "").toUpperCase()}
                          </div>
                          <span className={`pfd-pill mt-2 d-inline-block ${f.status === "ok" ? "pfd-ok" : "pfd-mute"}`}>
                            {f.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Product info */}
              {tab === "about" && (
                <div className="row g-3">
                  <div className="col-lg-7">
                    <div className="pfd-card p-3 p-lg-4 h-100">
                      <div className="fw-bold mb-2">{catalog?.title || product?.name}</div>
                      <p
                        className="mb-3"
                        style={{ fontSize: ".88rem", lineHeight: 1.65, color: "#44403C", whiteSpace: "pre-line", maxWidth: "68ch" }}
                      >
                        {catalog?.description || "No description available for this blank."}
                      </p>

                      <div className="row">
                        <div className="col-sm-6">
                          <Meta icon={<Tag size={15} />} label="Type" value={catalog?.type_name} />
                          <Meta icon={<Sparkle size={15} />} label="Brand / model" value={catalog?.brand || catalog?.model} />
                          <Meta
                            icon={<Sparkle size={15} />}
                            label="Technique"
                            value={(catalog?.techniques || []).map((t) => t.display_name).join(", ")}
                          />
                        </div>
                        <div className="col-sm-6">
                          <Meta icon={<Globe size={15} />} label="Origin" value={catalog?.origin_country} />
                          <Meta icon={<Ruler size={15} />} label="Catalog variants" value={catalog?.variant_count} />
                          <Meta
                            icon={<Warning size={15} />}
                            label="Status"
                            value={catalog?.is_discontinued ? "Discontinued" : "Active"}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-5">
                    <div className="pfd-card p-3 mb-3">
                      <div className="fw-bold mb-2 d-flex align-items-center gap-2" style={{ fontSize: ".88rem" }}>
                        <Palette size={15} weight="bold" aria-hidden="true" /> Colours &amp; sizes
                      </div>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {(summary?.colors || []).map((c) => {
                          const swatch = variants.find((v) => v.color === c)?.color_code;
                          return (
                            <span key={c} className="pfd-pill pfd-mute d-inline-flex align-items-center gap-1">
                              <span className="pfd-swatch" style={{ background: swatch || "#E7E5E4" }} />
                              {c}
                            </span>
                          );
                        })}
                        {(summary?.colors || []).length === 0 && (
                          <span style={{ fontSize: ".85rem", color: "#57534E" }}>—</span>
                        )}
                      </div>
                      <div className="d-flex flex-wrap gap-1">
                        {(summary?.sizes || []).map((s) => (
                          <span key={s} className="pfd-pill pfd-mute">{s}</span>
                        ))}
                      </div>
                    </div>

                    <div className="pfd-card p-3">
                      <div className="fw-bold mb-2" style={{ fontSize: ".88rem" }}>Print placements</div>
                      <div className="d-flex flex-wrap gap-1">
                        {(catalog?.placements || []).map((p) => (
                          <span key={p.id} className="pfd-pill pfd-mute">{p.title}</span>
                        ))}
                        {(catalog?.placements || []).length === 0 && (
                          <span style={{ fontSize: ".85rem", color: "#57534E" }}>—</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
